import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  DataGrid,
  GridSortModel,
  DataGridProps,
  GridFilterModel,
} from '@mui/x-data-grid';
import CustomTooltip from 'components/CustomTooltip';

import { IListRequestParams } from 'interfaces/requests';

import PORTUGUESE_LOCALE_TEXT from './translations';

interface IListProps extends DataGridProps {
  paginateAction?: (params: IListRequestParams) => void;
}

const PaginatedDataGrid: React.FC<IListProps> = ({
  disableColumnFilter = false,
  paginationMode = 'server',
  sortingMode = 'client',
  filterMode = 'server',
  paginateAction,
  ...props
}: IListProps): JSX.Element => {
  const dispatch = useDispatch();

  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [filter, setFilter] = React.useState<{ [key: string]: unknown }>();
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);

  const handleSortModelChange = (params: GridSortModel) => {
    const gridSortModel = params[0];
    const stateSortModel = sortModel[0];

    if (
      gridSortModel?.sort !== stateSortModel?.sort ||
      gridSortModel?.field !== stateSortModel?.field
    ) {
      setSortModel(params);
    }
  };

  const handlePageChange = (nextPageId: number) => {
    if (nextPageId !== page) {
      setPage(nextPageId);
    }
  };

  const handlePageSizeChange = (nextPageSize: number): void => {
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
    }
  };

  const handleFilterModelChange = (params: GridFilterModel): void => {
    const items = params.items[0];

    const parsedFilters =
      items.columnField && items.value
        ? { [items.columnField]: items.value }
        : {};

    if (JSON.stringify(parsedFilters) !== JSON.stringify(filter)) {
      setFilter(parsedFilters);
    }
  };

  useEffect(() => {
    if (paginateAction) {
      let field, sort;
      if (sortModel.length) {
        field = sortModel[0].field;
        sort = sortModel[0].sort;
      }

      dispatch(
        paginateAction({
          limit: paginationMode === 'server' ? pageSize : 999999,
          offset: paginationMode === 'server' ? pageSize * page : 0,
          orderBy: field,
          orderDirection: sort?.toString(),
          ...filter,
        }),
      );
    }
  }, [sortModel, filter, paginationMode]);

  if (props.columns) {
    props.columns.forEach((column) => {
      if (
        ![
          'actions',
          'delete',
          'collectRuleStrategies',
          'epsList',
          'EPSServicesType',
          'EPSServicesCategory',
          'exemptions.servicesCategories',
          'remunerationType',
          '',
          '_',
          '__',
          '__',
        ].includes(column.field)
      ) {
        let render =
          column.renderCell ?? column.valueFormatter ?? column.valueGetter;
        if (!render) render = (params) => params.value;
        column.valueFormatter = null;
        column.valueGetter = null;
        column.renderCell = (params) => {
          const rendered = render(params);
          return (
            <CustomTooltip
              title={rendered}
              disableHoverListener={typeof rendered !== 'string'}
            >
              <div>{rendered}</div>
            </CustomTooltip>
          );
        };
      }
    });
  }
  return (
    <DataGrid
      pageSize={pageSize}
      disableColumnFilter={disableColumnFilter}
      pagination
      paginationMode={paginationMode}
      sortingMode={sortingMode}
      filterMode={filterMode}
      rowsPerPageOptions={[5, 20, 50, 100]}
      onPageSizeChange={handlePageSizeChange}
      onPageChange={handlePageChange}
      onSortModelChange={handleSortModelChange}
      localeText={PORTUGUESE_LOCALE_TEXT}
      onFilterModelChange={handleFilterModelChange}
      disableSelectionOnClick
      disableColumnMenu
      sortModel={sortModel}
      {...props}
    />
  );
};

export default PaginatedDataGrid;
